import { memo, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { isEmpty, isNil, noop, values } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Types
import type { CJVisualizationDueDateUIModel } from 'types';
// Import UI Components
import { BusyLoader, Button, Checkbox } from '@geneui/components';
// Import Components
import StepsSetting from 'components/StepsSetting';
import CJFilterCondition from 'components/CustomerJourney/forms/CJFilterCondition';
import CustomerJourneyVisualizationDueDate from './CustomerJourneyVisualizationDueDate';
import CustomerJourneyVisualizationTemplateGroup from './CustomerJourneyVisualizationTemplateGroup';
import { SelectLanguageWithSearchModal } from 'components/common/Modals';
import SectionWithTitle from 'components/common/SectionWithTitle';
// Import Helpers
import { itemsIsValid } from 'services/Helpers';
// Import Constants
import { LogicTypesValues, l } from 'constants/common';
// Import Styles
import styles from './customerJourneyVisualizationTarget.module.scss';

interface DefaultValueCondition {
    filters: AnyObject[];
    logic?: string;
    isValid?: boolean;
}

interface Props {
    defaultValue?: {
        condition?: DefaultValueCondition;
        templates?: AnyObject;
        dueDate?: Nilable<CJVisualizationDueDateUIModel>;
    };
    node?: Nullable<AnyObject>;
    elements?: Nullable<AnyObject[]>;
    option?: Nullable<AnyObject>;
    dueDateOption?: Nullable<AnyObject>;
    collectedProperties?: AnyObject[];
    getUpdate?: (data: AnyObject) => void;
    isLive?: AnyObject;
    isLiveChangeHandler?: (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
    selectedLanguages?: string[];
    selectedLanguagesChangeHandler?: (languages: string[]) => void;
    stepSettingsData?: AnyObject[];
    stepSettingChangeHandler?: (data: AnyObject[]) => void;
}

export interface CustomerJourneyVisualizationTargetRef {
    changeLanguages: (languages: string[], partnerLanguages: AnyObject) => void;
}

const CustomerJourneyVisualizationTarget = forwardRef<CustomerJourneyVisualizationTargetRef, Props>((props, ref) => {
    const {
        collectedProperties = [],
        node = null,
        elements = null,
        option = null,
        dueDateOption = null,
        getUpdate = noop,
        defaultValue = {
            condition: {
                logic: LogicTypesValues.and,
                filters: [],
            },
            templates: {},
        },
        isLive,
        isLiveChangeHandler,
        selectedLanguagesChangeHandler,
        selectedLanguages,
        stepSettingsData,
        stepSettingChangeHandler,
    } = props;
    const { t } = useTranslation();

    const [filterConditionFilters, setFilterConditionFilters] = useState<Nilable<DefaultValueCondition>>(
        defaultValue?.condition,
    );
    const [templateData, setTemplateData] = useState<Nilable<AnyObject>>(defaultValue?.templates);
    const [dueDate, setDueDate] = useState<Nilable<CJVisualizationDueDateUIModel>>(defaultValue?.dueDate);

    const [partnerLanguageModalIsOpen, setPartnerLanguageModalIsOpen] = useState(false);
    const [showStepSetting, setShowStepSetting] = useState(false);

    useImperativeHandle(ref, () => ({
        changeLanguages(languages, partnerLanguages) {
            setTemplateData((prev) => {
                return languages.reduce(
                    (acc, language) => {
                        if (isNil(prev?.[language])) {
                            acc[language] = {
                                name: '',
                                description: '',
                                language: { flagId: language, label: partnerLanguages[language]?.Name },
                            };
                        } else {
                            acc[language] = prev?.[language];
                        }
                        return acc;
                    },
                    { defaultLang: prev?.defaultLang },
                );
            });
        },
    }));

    const conditionUpdateHandler = (filterValue) => {
        setFilterConditionFilters((prev) => ({ ...prev, ...filterValue }));
    };

    const templateChangeHandler = (languageKey, data) => {
        setTemplateData((prev) => {
            return { ...prev, [languageKey]: { ...prev?.[languageKey], ...data } };
        });
    };

    const dueDateUpdateHandler = (dueDateValue: Partial<CJVisualizationDueDateUIModel>) => {
        setDueDate((prev) => ({ ...prev, ...dueDateValue }) as CJVisualizationDueDateUIModel);
    };

    useEffect(() => {
        const isValidTemplates = itemsIsValid(values(templateData));
        const isDueDateValid = isNil(dueDate) || dueDate?.isValid === true;

        getUpdate({
            isValid: isValidTemplates && filterConditionFilters?.isValid && isDueDateValid,
            condition: filterConditionFilters,
            templates: templateData,
            dueDate: dueDate,
        });
        // DEPS: [getUpdate]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterConditionFilters, templateData, dueDate]);

    const handleModalIsVisible = () => {
        setPartnerLanguageModalIsOpen(!partnerLanguageModalIsOpen);
    };

    const handleOnOkModal = (values) => {
        setPartnerLanguageModalIsOpen(!partnerLanguageModalIsOpen);
        const tmpSelectedLanguages = values.map((item) => item?.data?.Id);
        selectedLanguagesChangeHandler?.(tmpSelectedLanguages);
    };

    const stepSettingClickHandler = () => {
        setShowStepSetting(true);
    };

    const stepSettingCloseHandler = () => {
        setShowStepSetting(false);
    };

    const stepSettingOkHandler = (data) => {
        setShowStepSetting(false);
        stepSettingChangeHandler?.(data);
    };

    return (
        <div className="crm-cj-visualization-step">
            <SectionWithTitle
                title={t(l.GeneralDetails)}
                titleContent={
                    <div className={styles.targetActions}>
                        <Button appearance="minimal" icon="bc-icon-languages-48" onClick={handleModalIsVisible}>
                            {t(l.SelectLanguage)}
                        </Button>
                        <Checkbox
                            label={t(l.IsLive)}
                            checked={isLive?.value}
                            onChange={isLiveChangeHandler}
                            disabled={isLive?.isValid !== true}
                        />
                        <div className={styles.targetActionsSettings}>
                            <Button icon="bc-icon-settings" appearance="minimal" onClick={stepSettingClickHandler} />
                        </div>
                    </div>
                }
            >
                <BusyLoader isBusy={isEmpty(elements) || isNil(node) || isNil(option)}>
                    {partnerLanguageModalIsOpen && (
                        <SelectLanguageWithSearchModal
                            onCancel={handleModalIsVisible}
                            onOk={handleOnOkModal}
                            languageList={selectedLanguages}
                        />
                    )}
                    {!isEmpty(elements) && !isNil(node) && !isNil(option) && (
                        <>
                            <CustomerJourneyVisualizationTemplateGroup
                                templates={templateData}
                                onChange={templateChangeHandler}
                            />
                            <CJFilterCondition
                                hasSectionLayout={true}
                                collectedProperties={collectedProperties}
                                node={node}
                                elements={elements}
                                option={option}
                                getUpdate={conditionUpdateHandler}
                                defaultValue={filterConditionFilters}
                                groupLabel={t(l.Filters)}
                            />
                            {!isNil(dueDate) && (
                                <CustomerJourneyVisualizationDueDate
                                    collectedProperties={collectedProperties}
                                    node={node}
                                    elements={elements}
                                    option={dueDateOption}
                                    getUpdate={dueDateUpdateHandler}
                                    defaultValue={dueDate}
                                />
                            )}
                        </>
                    )}
                </BusyLoader>
            </SectionWithTitle>
            {showStepSetting && (
                <StepsSetting
                    isVisible={showStepSetting}
                    onCancel={stepSettingCloseHandler}
                    onOk={stepSettingOkHandler}
                    steps={stepSettingsData}
                    columnName={l.Group}
                    titleLabel={l.GroupsSetting}
                    listTitleLabel={l.Groups}
                />
            )}
        </div>
    );
});

CustomerJourneyVisualizationTarget.displayName = 'CustomerJourneyVisualizationTarget';

export default memo(CustomerJourneyVisualizationTarget);
